import {
  ToastType,
  ToastProps,
} from "../components/common/Banner/ToastContainer";
import { useToast } from "../components/common/Banner/ToastContainer";

// Hook-based usage with full props support
export const useCustomToast = () => {
  const { showToast } = useToast();

  // Basic version maintains backward compatibility
  const triggerToast = (
    message: string,
    type: ToastType = "success",
    duration = 5000,
    position?: "top" | "bottom", // Optional position parameter
  ) => {
    showToast({
      message,
      type,
      duration,
      position: position || "top", // Use provided position or default
    });
  };

  // Enhanced version accepts all ToastProps
  const triggerToastWithProps = (props: ToastProps) => {
    // Merge default position with provided props
    showToast({
      position: props.position || "top", // Use provided position or default
      ...props,
    });
  };

  return {
    showToast: triggerToast,
    showToastWithProps: triggerToastWithProps,
  };
};

// Global usage from outside React components
let toastRef: ((props: ToastProps) => void) | null = null;

export const setToastHandler = (handler: (props: ToastProps) => void) => {
  toastRef = handler;
};

// Enhanced toast function that accepts all props
export const showToast = (
  message: string,
  type: ToastType = "success",
  duration: number = 5000, // Default duration
  position?: "top" | "bottom", // Optional position parameter
  options?: Omit<ToastProps, "message" | "type" | "duration" | "position">,
) => {
  if (toastRef) {
    toastRef({
      message,
      type,
      duration,
      position: position || "top", // Use provided position or default
      ...options,
    });
  } else {
    console.warn("Toast handler not initialized");
  }
};

// Enhanced toast function that accepts all props
export const showToastWithProps = (props: ToastProps) => {
  if (toastRef) {
    toastRef(props);
  } else {
    console.warn("Toast handler not initialized");
  }
};

// ... APPLICATION Example usage ...

// ... Outside React components with hook ...
// import { showToast, showToastWithProps } from "../../utils/toastUtils";

// Inside React components with hook
// import { useCustomToast } from "../../utils/toastUtils";
// const { showToast, showToastWithProps } = useCustomToast();

// showToast("Invoice created error.", "error");
// showToast("Invoice created info.", "info", 10000);
// showToast("Invoice created error.", "error", 5000, "top");
// showToast("Invoice created successfully.", "success", 2000, "top");
// showToastWithProps({
//   message: "Operation successful",
//   type: "success",
//   duration: 5000,
//   position: "bottom",
//   actionText: "Click Me",
//   onActionClick: () => console.log("sample click"),
//   onClose: () => console.log("Toast closed"),
// });
